import React, { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type { RewindGraphGridParams } from '../hooks/useRewindGraphGridParams';

import styles from './RewindGraphHeightAxis.module.scss';

const PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS = 3;

export const calculateMaxWaveHeight = (maxHeight: number, waveUnits: string) => {
  if (waveUnits.toLowerCase() === 'm') {
    return Math.ceil(maxHeight * 10) / 10;
  }
  if (maxHeight <= 4) return Math.ceil(maxHeight);
  return (
    Math.ceil(maxHeight / PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS) *
    PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS
  );
};

const descendingNumbers = Array(PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS)
  .fill(0)
  .map((_, index) => PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS - index);

export const axisLabelsFt = (roundedMaxHeight: number) => {
  switch (roundedMaxHeight) {
    case 1:
      return [1];
    case 2:
      return [2, 1];
    case 3:
      return [3, 2, 1];
    case 4:
      return [4, 2];
    default:
      return descendingNumbers.map(
        (i) => i * (roundedMaxHeight / PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS),
      );
  }
};

export const calculateAxisLabels = (maxHeight: number, waveUnits: string) => {
  if (waveUnits.toLowerCase() === 'm') {
    return descendingNumbers.map(
      (i) => Math.ceil(((maxHeight * 10) / PREFERRED_NUMBER_OF_HEIGHT_AXIS_LABELS) * i) / 10,
    );
  }
  return axisLabelsFt(maxHeight);
};

export const RewindGraphHeightAxis = ({
  roundedMaxWaveHeight,
  waveUnits,
  graphGridParameters,
}: {
  roundedMaxWaveHeight: number;
  waveUnits: string;
  graphGridParameters: RewindGraphGridParams;
}) => {
  const heightAxisLabels = useMemo(
    () => calculateAxisLabels(roundedMaxWaveHeight, waveUnits),
    [roundedMaxWaveHeight, waveUnits],
  );

  const labelUnits = useMemo(() => (waveUnits.toLowerCase() === 'm' ? 'm' : 'ft'), [waveUnits]);

  return (
    <>
      <Box
        gridColumn={graphGridParameters.heightAxisColumn}
        gridRow={graphGridParameters.chartAreaRows}
        height="100%"
        width="100%"
      >
        <Box
          gridTemplateRows={`repeat(${heightAxisLabels.length * 2 + 1}, 1fr)`}
          display="grid"
          height="100%"
          width="100%"
        >
          {heightAxisLabels.map((height, index) => (
            <React.Fragment key={height}>
              <Box
                data-testid="wave-insights-graph-height-axis-labels"
                className={styles.heightAxisLabelContainer}
                gridColumn={1}
                gridRow={`${index * 2 + 1} / ${index * 2 + 3}`}
              >
                <Typography variant="caption2" className={styles.heightAxisLabels}>
                  {height}
                  {labelUnits}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Box
        gridColumn={graphGridParameters.chartAreaColumns}
        gridRow={graphGridParameters.chartAreaRows}
        height="100%"
      >
        <Box
          gridTemplateRows={`repeat(${heightAxisLabels.length * 2 + 1}, 1fr)`}
          display="grid"
          height="100%"
        >
          {heightAxisLabels.map((height, index) => (
            <React.Fragment key={height}>
              <Box
                className={styles.heightAxisGridLines}
                data-testid="wave-insights-graph-height-axis-gridlines"
                gridColumn={1}
                gridRow={2 + index * 2}
              />
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default memo(RewindGraphHeightAxis);
